import { useState } from 'react';
import Button from '../../components/Button';
import type { StreakFreezes, StreakPatch, StreakState } from './streakService';

export const inputClass = 'w-full rounded-md border border-gray-300 px-3 py-2 disabled:bg-gray-100';
export const cardClass = 'rounded-lg border border-gray-200 bg-white p-4';

export const FreezeFields = ({ value, onChange }: { value: StreakFreezes; onChange: (value: StreakFreezes) => void }) => (
    <div className="grid gap-4 sm:grid-cols-2">
        <label className="text-sm">Available freezes
            <input className={inputClass} type="number" min={0} max={3} step={1} required value={value.available}
                onChange={e => onChange({ available: e.target.valueAsNumber, progressDays: e.target.valueAsNumber === 3 ? 0 : value.progressDays })} />
        </label>
        <label className="text-sm">Progress days toward next freeze
            <input className={inputClass} type="number" min={0} max={2} step={1} required value={value.progressDays}
                disabled={value.available === 3} onChange={e => onChange({ ...value, progressDays: e.target.valueAsNumber })} />
        </label>
    </div>
);

export const StreakStateEditor = ({ state, disabled, onSave }: {
    state: StreakState; disabled: boolean; onSave: (patch: StreakPatch) => Promise<void>;
}) => {
    const [streak, setStreak] = useState(state.streak);
    const [freezes, setFreezes] = useState(state.streakFreezes);
    return <div className="grid gap-4 lg:grid-cols-2">
        <form className={cardClass} onSubmit={e => { e.preventDefault(); void onSave({ streakFreezes: freezes }); }}>
            <h2 className="mb-3 text-xl font-semibold">Streak freezes</h2>
            <fieldset disabled={disabled} className="space-y-4">
                <FreezeFields value={freezes} onChange={setFreezes} />
                <p className="text-sm text-gray-600">Every 3 qualifying days earns a freeze. At 3 freezes, progress pauses at zero. Saving here preserves activity history.</p>
                <Button type="submit" disabled={disabled}>Save freezes</Button>
            </fieldset>
        </form>
        <form className={cardClass} onSubmit={e => {
            e.preventDefault();
            void onSave({ streak: { current: streak.current, longest: streak.longest, lastDay: streak.lastDay, runStartDay: streak.runStartDay, protectedThrough: streak.protectedThrough } });
        }}>
            <h2 className="mb-3 text-xl font-semibold">Stored streak summary</h2>
            <fieldset disabled={disabled} className="space-y-4">
                <div className="grid gap-3 sm:grid-cols-3">
                    <label className="text-sm">Current
                        <input className={inputClass} type="number" required min={0} max={100000} step={1} value={streak.current}
                            onChange={e => setStreak({ ...streak, current: e.target.valueAsNumber })} />
                    </label>
                    <label className="text-sm">Longest
                        <input className={inputClass} type="number" required min={streak.current} max={100000} step={1} value={streak.longest}
                            onChange={e => setStreak({ ...streak, longest: e.target.valueAsNumber })} />
                    </label>
                    <label className="text-sm">Last active day
                        <input className={inputClass} type="date" min="2000-01-01" max={state.dayKey} required={streak.current > 0} value={streak.lastDay ?? ''}
                            onChange={e => setStreak({ ...streak, lastDay: e.target.value || null })} />
                    </label>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                    <label className="text-sm">Current run start
                        <input className={inputClass} type="date" max={streak.lastDay ?? state.dayKey} value={streak.runStartDay ?? ''}
                            onChange={e => setStreak({ ...streak, runStartDay: e.target.value || null })} />
                    </label>
                    <label className="text-sm">Protected through
                        <input className={inputClass} type="date" min={streak.lastDay ?? undefined} max={state.dayKey} value={streak.protectedThrough ?? ''}
                            onChange={e => setStreak({ ...streak, protectedThrough: e.target.value || null })} />
                    </label>
                </div>
                <p className="text-sm text-gray-600">This changes only the summary and clears its pending notification. Use a scenario below to keep calendar days and counts consistent with the streak.</p>
                <Button type="submit" disabled={disabled}>Save summary</Button>
            </fieldset>
        </form>
    </div>;
};
